import { type Command, type CommandHandler, CommandName, IMediator } from "@telespot/core";

export interface MediatorProvider<H extends Record<CommandName, () => CommandHandler>> {
  provider: Readonly<H>;
}

export interface MediatorHandlers<H extends Record<CommandName, () => CommandHandler>> {
  handlers: Readonly<Record<keyof H, CommandHandler<Command<keyof H>>>>;
}

/**
 * Inprocess implementation of both {@link IMediator}
 */
export class Mediator<H extends Record<CommandName, () => CommandHandler>>
  implements IMediator<Record<keyof H, CommandHandler<Command<keyof H>>>> {

  private readonly provider: Partial<H>;
  private readonly handlers: Partial<Record<keyof H, CommandHandler<Command<keyof H>>>>;

  constructor(config: MediatorProvider<H>)
  constructor(config: MediatorHandlers<H>)
  constructor(config: MediatorProvider<H> | MediatorHandlers<H>) {
    if (this.isProviderConfig(config)) {
      this.provider = config.provider;
      this.handlers = {};
    } else if (this.isHandlerConfig(config)) {
      this.provider = {};
      this.handlers = config.handlers;
    } else {
      this.provider = {};
      this.handlers = {};
    }
  }

  private isProviderConfig(config: object): config is MediatorProvider<H> {
    return 'provider' in config;
  }

  private isHandlerConfig(config: object): config is MediatorHandlers<H> {
    return 'handlers' in config;
  }

  public send<T extends keyof H>(command: Command<T>): ReturnType<ReturnType<H[T]>["execute"]> {
    const handler = this.handlers[command.name] ??= this.provider[command.name]?.();

    if (!handler) throw new Error(`[Mediator] No handler defined for command: ${String(command.name)}`);

    return handler.execute(command);
  }

  public isSupported(command: Command): command is Command<keyof H> {
    return command.name in this.provider || command.name in this.handlers;
  }
}
